import { useEffect } from "react";
import { useForm, SubmitHandler } from "react-hook-form";
import Grid from "@mui/material/Grid";

import { usePage } from "../../../hooks/usePage";
import { useEditPage } from "../../../hooks/useEditPage";
import { PageEdit, PageEditConstructor } from "../../../models/PageEdit";

import FormCardWrapper from "../../../../../components/reusable-components/form/FormCardWrapper";
import FormTextField from "../../../../../components/reusable-components/form/MuiFormInput";
import RichTextForm from "../../../../../components/reusable-components/form/RichTextForm";
import SubmitButton from "../../../../../components/reusable-components/form/SubmitButton";
import DomainSelectForm from "../../../../domain/components/form/DomainSelectForm";
import FormFileInput from "../../../../../components/reusable-components/form/FormFileInput";

import { useApiFieldErrors } from "../../../../../hooks/fetch/useApiFieldErrors";
import { useRefetchOnSuccess } from "../../../../../hooks/fetch/useRefetchOnSuccess";
import { useFieldRules } from "../../../../../hooks/useFieldRules";
import CategorySelectForm from "../../../../categories/components/CategorySelectForm";
import { Box, CardMedia } from "@mui/material";

const EditPageForm = ({ id }: { id: string }) => {
  const {
    fetch: { data: pageData, loading, error, refetch },
  } = usePage({ id });

  const {
    fetch: {
      fetchData: updatePage,
      loading: updating,
      error: updateError,
      success: updateSuccess,
    },
  } = useEditPage({ id });

  const rules = useFieldRules();

  const {
    handleSubmit,
    formState: { isSubmitting },
    setError,
    reset,
    clearErrors,
    control,
    setValue,
    watch,
  } = useForm<PageEditConstructor>({
    defaultValues: {
      title: "",
      slug: "",
      body: "",
      domain_id: undefined,
      category_id: undefined,
      featured_image: null,
      featured_image_file: undefined,
    },
    mode: "all",
  });

  useEffect(() => {
    if (pageData) {
      reset({
        ...pageData,
        // title: pageData.title,
        domain_id: {
          label: pageData.domain?.name ?? pageData.domain_id,
          value: pageData.domain_id,
        },
        category_id: {
          label: pageData.category?.name ?? pageData.category_id,
          value: pageData.category_id,
        },
      });
    }
  }, [pageData, reset]);

  useRefetchOnSuccess(updateSuccess, refetch);
  useApiFieldErrors(updateError, setError);

  const onSubmit: SubmitHandler<PageEditConstructor> = async (formData) => {
    clearErrors();

    if (!pageData) return;

    const payload = new PageEdit({
      ...formData,
      full_url: pageData.full_url,
      stripe_product_id: pageData.stripe_product_id,
      monthly_price: pageData.monthly_price,
      yearly_price: pageData.yearly_price,
    });

    await updatePage({ body: payload });
  };

  const isLoading = loading || updating || isSubmitting;
  const featuredImage = watch("featured_image");

  return (
    <FormCardWrapper
      title="Edit Page"
      successText="Page updated successfully!"
      success={updateSuccess}
      error={error || updateError}
      onSubmit={handleSubmit(onSubmit)}
    >
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 6 }}>
          <FormTextField
            name="title"
            label="Title"
            control={control}
            textFieldProps={{ disabled: isLoading }}
            rules={rules("Title").required().get()}
          />
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <FormTextField
            name="slug"
            label="Slug"
            control={control}
            textFieldProps={{ disabled: isLoading }}
            rules={rules("Slug").required().slug().get()}
          />
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <DomainSelectForm
            name="domain_id"
            control={control}
            disabled={true}
            controllerProps={{
              rules: rules("Domain").required().get(),
            }}
          />
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <CategorySelectForm
            name="category_id"
            control={control}
            setValue={setValue}
            watch={watch}
            domainId={watch("domain_id")}
            disabled={isLoading || !watch("domain_id")?.value}
            controllerProps={{
              rules: rules("Category").required().get(),
            }}
          />
        </Grid>

        <Grid size={{ xs: 12 }}>
          <FormTextField
            name="featured_image"
            label="Featured Image LINK"
            control={control}
            textFieldProps={{
              disabled: isLoading,
            }}
            rules={rules("Featured Image LINK").url().get()}
          />
        </Grid>
        {featuredImage && (
          <Box mt={2}>
            <CardMedia
              component="img"
              height="200"
              image={featuredImage}
              alt="Featured Preview"
              sx={{
                borderRadius: 2,
                border: "1px solid #ccc",
                objectFit: "contain",
                maxWidth: "100%",
              }}
            />
          </Box>
        )}

        <Grid size={12}>
          <RichTextForm
            name="body"
            label="Body"
            control={control}
            disabled={isLoading}
            rules={rules("Body").required().get()}
          />
        </Grid>

        <Grid size={{ xs: 6 }}>
          <FormTextField
            name="full_url"
            label="Full URL"
            control={control}
            textFieldProps={{
              disabled: true,
            }}
          />
        </Grid>

        <Grid size={{ xs: 6 }}>
          <FormTextField
            name="stripe_product_id"
            label="Stripe Product ID"
            control={control}
            textFieldProps={{
              disabled: true,
            }}
          />
        </Grid>

        <Grid size={{ xs: 6 }}>
          <FormTextField
            name="monthly_price"
            label="Monthly Price"
            control={control}
            textFieldProps={{
              disabled: true,
            }}
          />
        </Grid>

        <Grid size={{ xs: 6 }}>
          <FormTextField
            name="yearly_price"
            label="Yearly Price"
            control={control}
            textFieldProps={{
              disabled: true,
            }}
          />
        </Grid>

        <Grid size={12}>
          <SubmitButton
            text="Update Page"
            loading={isLoading}
            disabled={isLoading}
          />
        </Grid>
      </Grid>
    </FormCardWrapper>
  );
};

export default EditPageForm;
